/**
 * What an install that has said nothing is configured with, and the bounds every
 * value is clamped to (§4.1). The account area holds an organization's defaults
 * to the same bounds (§5.25): a default the app would clamp is a default the page
 * refuses.
 */
import { EMPTY_EXTENSIONS } from './extensions';
import { AGENT_IDS } from './agents';
import { BASE_DOCKERFILE } from './dockerfile';
import { DEFAULT_NAMESERVERS } from './resolve';
import type { AppSettings } from './types';

export const DEFAULT_SETTINGS: AppSettings = {
  appearance: 'dark',
  confirmDestroy: true,
  // A fresh install has not been set up. An install that predates the setup
  // has no row for this either — the shell treats a workspace already there as
  // the answer, so nobody who has been using the app meets a first launch.
  setupDone: false,
  preventSleep: true,
  // What the editor did before it had preferences — wrapped lines, CodeMirror's
  // own keys — plus the two aids every IDE draws by default, and the servers on:
  // nothing installs itself, so a missing one is only ever offered (§5.4).
  editor: {
    keymap: 'standard',
    wrap: true,
    whitespace: false,
    indentGuides: true,
    minimap: false,
    formatOnSave: false,
    languageServers: true
  },

  // Both, on an install that has said nothing: the app knows how to launch two
  // agents and offering only one would be a decision nobody made (§5.10).
  agents: [...AGENT_IDS],
  defaultModel: 'opus',
  // The CLI's own default (§9.3), so a session started without touching this
  // reasons as deeply as one launched with no `--effort` at all — which is what
  // every session got while the flag was unimplemented.
  defaultEffort: 'xhigh',
  defaultSourceBranch: 'main',
  // One instruction, as a hand-started session has always been (§5.15); every
  // automatic step below is off until somebody asks for it.
  defaultWorkflowId: '',
  autoAcceptPlan: false,
  // Three agents is what a Max subscription sustains comfortably (§11.4).
  maxWorkingSessions: 3,
  // More than the pipeline is wide, so finishing a session and leaving it for
  // review does not stall the queue — but bounded, because every one of these
  // holds its vmMemoryMb whether or not anything is thinking in it.
  maxLiveSandboxes: 6,
  // Longer than reading a diff carefully takes, short enough that a board left
  // to run all afternoon hands back the memory of everything that finished
  // early (§11.4).
  parkAfterMinutes: 30,
  archiveRetentionDays: 30,

  // Off until someone signs in: an integration nobody configured must not be a
  // timer nobody asked for (§5.21).
  jiraEnabled: false,
  jiraHost: '',
  jiraEmail: '',
  trelloEnabled: false,
  // On, unlike the two above: it signs in to nothing and reaches nothing, so
  // there is no timer to start and no credential to read by leaving it on.
  bookmarkletEnabled: true,
  // On and local: the rows stay on this machine unless an organization this
  // install belongs to publishes the setting on (`usageStatsEnabled`).
  usageStatsEnabled: true,
  // Off: a run is the organization's to record only once it publishes this on,
  // which its onboarding then hands every member (`runTrailEnabled`).
  runTrailEnabled: false,
  // Often enough that a ticket assigned before a standup is on the board after
  // it, rarely enough that it is not a request every time the window has focus.
  taskSyncMinutes: 10,

  sandboxBackend: 'auto',
  dockerfile: BASE_DOCKERFILE,
  containerRuntime: 'none',
  // Sized for a workspace, not for a project. A session is every repository the
  // person works on at once (§5.4), and one of those repositories is routinely a
  // JVM service that wants most of a gigabyte to itself before it serves a
  // request — five of them beside a bundler and the agent's own toolchain fitted
  // into 2 vCPU / 4 GB by swapping, which is what "the whole app is slow" was.
  // Still a ceiling rather than a reservation: a micro-VM boots at 3 GB and
  // grows toward it only while the guest runs short (§8.5), and the desktop
  // caps it at half the host. Per-workspace overridable, like everything else
  // here (§4.1).
  vmCpu: 6,
  vmMemoryMb: 12288,
  // 10 GB, not 20. The disk is what a snapshot is a copy of, and there are more
  // snapshots now than there used to be — the warm image is one per workspace per
  // dependency set (§8.6) on top of one per Dockerfile. A session's own tree, its
  // toolchain and its dependency caches fit inside 10 with room to spare; what
  // filled 20 was never the workspace, it was the provisioned size itself.
  vmDiskGb: 10,
  warmImagesKept: 5,
  // Half a gigabyte: well above a repository worked on by hand; a person whose
  // history weighs more raises it in Settings › Sandbox (§5.4).
  repoSeedMaxMb: 512,
  agentPanelPlacement: 'sandbox',
  dockerDiskGb: 20,

  autonomous: true,
  agentConfigSource: 'host',
  // Current by default: a snapshot outlives the release it was built from, and a
  // `claude` weeks behind is a session that behaves unlike the one on the host
  // (§10.2). Pinning is one setting away for a workspace that needs the image to
  // be the whole answer.
  agentUpdate: 'latest',
  // Nothing set: each agent reads the host's config, or its own defaults (§5.8).
  agentSettings: { 'claude-code': {}, codex: {} },
  statusLineScript: '',
  // Nothing, and that is the answer for a fresh install: an agent arrives with
  // what its own CLI ships, and every line here is one somebody added (§5.26).
  agentExtensions: EMPTY_EXTENSIONS,
  idleHintMs: 20_000,

  writingLanguages: { commit: 'en', workflow: 'en', changeMap: 'en', pullComments: 'en' },
  // Off: masking rewrites what the person typed, which is a choice they make
  // knowing the agent then works on placeholders (§7.5).
  privacyMask: false,
  privacyTerms: [],

  commitCoAuthoredBy: false,
  // On: the review asks for this sync before any merge anyway, and a conflicted
  // one is abandoned rather than left stopped (§5.5).
  autoSync: true,
  autoLand: { branch: false, message: false, push: false, pullRequest: false, session: 'off' },
  // The audit costs a turn of the agent's own per hand-back with findings — worth it for the ones that matter.
  audit: { auto: 'serious', mapModel: '', mapEffort: '', blockRules: 'enforced', blockFindings: 'high' },
  // On by default and free while nothing uses it: no binary is downloaded and
  // no credential is read until a repository with a git-provider remote is looked at
  // (§5.16).
  gitEnabled: true,

  // Off by default, and unlike `gitEnabled` that is not only about cost: a
  // tunnel puts a port of a running session on the public internet (§5.17). It
  // is a decision, so it is one somebody makes rather than one they inherit.
  tunnelsEnabled: false,
  // Empty means "wherever it usually is" — Airlock never installs an agent, so
  // this is how a machine that keeps one somewhere unusual says where (§5.17).
  tunnelBinPath: '',

  // On by default, and the reason it differs from `tunnelsEnabled` is the
  // boundary: this reaches only ports the session already publishes to itself
  // (§5.18). Checking your own work is the ordinary case, not the exception.
  agentPreview: true,
  agentPreviewLogins: true,
  // Free until a probe is set, so on wherever Preview is; the start that costs
  // something — `ready` — is a person's choice per workspace (§5.23).
  agentProbes: 'attach',

  extraDomains: [],
  // Public, and pinned rather than inherited from the host — see
  // `DEFAULT_NAMESERVERS`. A network that permits only its own resolvers is what
  // this setting exists for, and it says so by listing them here.
  nameservers: [...DEFAULT_NAMESERVERS],
  // Onboarding a new project is where the allowlist hurts: the app is correct,
  // the call is necessary, and it fails. `ask` keeps the decision a person's
  // without making them restart the session to apply it (§7.1).
  egressMode: 'ask',

  // Everything, by default. A session that runs while you do something else is
  // the whole premise (§1.2), and it is the one setting where the cautious
  // default is the useless one: an app you have to keep watching to learn that
  // it stopped watching for you has not saved anybody anything. Turning it down
  // is one row, and it is per workspace (§12).
  notifications: 'all',

  logRetentionMb: 256,
  logBufferLines: 2000
};

/** Bounds are the app's, not the user's: a session must stay startable. */
export const LIMITS = {
  maxWorkingSessions: [1, 10],
  maxLiveSandboxes: [1, 30],
  parkAfterMinutes: [0, 1440],
  archiveRetentionDays: [1, 365],
  taskSyncMinutes: [2, 120],
  vmCpu: [1, 16],
  vmMemoryMb: [1024, 65_536],
  vmDiskGb: [5, 500],
  // One at least: the sweep runs right after a build, and a budget of none
  // would delete the image that build had just committed.
  warmImagesKept: [1, 20],
  // Up to 8 GB: the bundle and the clone made from it both sit on the session
  // disk, which itself stops at 500 GB but defaults to 10.
  repoSeedMaxMb: [64, 8192],
  dockerDiskGb: [5, 500],
  idleHintMs: [5_000, 600_000],
  logRetentionMb: [16, 8192],
  logBufferLines: [200, 50_000]
} as const;
